import { ViewFile } from '@/components/ats';
import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { KText } from '@/ui-library/components/text';
import { Center, Flex } from '@chakra-ui/react';
import { useMemo } from 'react';
import { type InterviewType } from '../types/interview';

export const InterviewResume = ({
  interview,
  refetch,
}: {
  interview: InterviewType;
  refetch?: () => void;
}) => {
  const { resume = {} } = interview?.candidate || {};
  // Keep the iframe `src` referentially stable across unrelated re-renders/polls
  // for the same presigned URL (no reload flicker), but key on the URL itself so
  // a freshly-minted URL from a refetch flows through. Freezing on `resume.id`
  // used to pin the very first URL forever, which then expired mid-session and
  // 403'd ("unable to load") for interviewers who kept the tab open (ENG-3354).
  // `PDFReaderBrowser` re-mints via `onRefreshUrl` before the URL actually lapses.
  const stableResume = useMemo(
    () => ({
      url: resume?.url,
      content_type: resume?.content_type,
      preview_url: resume?.preview?.url,
    }),
    [resume?.url, resume?.content_type, resume?.preview?.url]
  );

  const isPreviewAvailable =
    [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/x-ole-storage',
    ]?.includes(stableResume.content_type) && stableResume.preview_url;

  if (stableResume.content_type !== 'application/pdf' && !isPreviewAvailable)
    return (
      <Center h="100%" flexDir="column" gap="12px">
        <KText fontSize="sm">Resume preview unavailable for this format</KText>
        <ViewFile file={resume} showPreview={false} onRefresh={refetch} />
      </Center>
    );

  return (
    <Flex w="100%" h="100%" flexDir="column">
      <PDFReaderBrowser
        file={stableResume.url}
        content_type={stableResume.content_type}
        preview_url={stableResume.preview_url}
        fileName={interview?.candidate?.name}
        showReplaceControl={false}
        onRefreshUrl={refetch}
        styles={{
          container: {
            border: 'none',
          },
        }}
      />
    </Flex>
  );
};
